import { Suspense, lazy } from "react";
import type { RouteObject } from "react-router-dom";
import { Navigate } from "react-router-dom";
import RequireAuth from "@components/RequiredAuth";
import RequireGuest from "@components/RequireGuest";
import RequirePlaygroundAccess from "@components/RequiredAuth/RequirePlaygroundAccess";
import RequirePortalLabAccess from "@components/RequiredAuth/RequirePortalLabAccess";
import RequireCognitoFlow from "@components/RequireCognitoFlow";
import Spinner from "@components/Spinner";

const Layout = lazy(() => import("./layout/DefaultLayout"));
const DemoLab = lazy(() => import("@pages/demo-lab"));
const DemoLabCreate = lazy(() => import("@pages/demo-lab/create"));
const Settings = lazy(() => import("@pages/settings"));
const ReadMe = lazy(() => import("@pages/read-me"));
const PortalLabLayout = lazy(() => import("@pages/portal-lab"));
const PortalLabList = lazy(() => import("@pages/portal-lab/PortalLabList"));
const PortalLabHome = lazy(() => import("@pages/portal-lab/PortalLabHome"));
const PortalLabDefinePortal = lazy(() => import("@pages/portal-lab/create"));
const PageNotFound = lazy(() => import("@pages/not-found"));
const Playground = lazy(() => import("@pages/playground"));
// const MockupLab = lazy(() => import("@pages/mockup-lab"));
// const MockupLabCreate = lazy(() => import("@pages/mockup-lab/create"));
// const MockupLabView = lazy(() => import("@pages/mockup-lab/view"));
const Integration = lazy(() => import("@pages/integration"));
const CreateConnection = lazy(() => import("@pages/integration/create/connection"));
const CreateStack = lazy(() => import("@pages/integration/create/stack"));
const EditConnection = lazy(() => import("@pages/integration/edit/connection"));
const EditStack = lazy(() => import("@pages/integration/edit/stack"));
const AuthLogin = lazy(() => import("@pages/auth/LoginPage"));
const AuthSignUp = lazy(() => import("@pages/auth/SignupPage"));
const AuthForgotPassword = lazy(() => import("@pages/auth/ForgotPasswordPage"));
const AuthEmailVerified = lazy(() => import("@pages/auth/EmailVerifiedPage"));
const AuthAuthenticationCode = lazy(() => import("@pages/auth/AuthenticationCodePage"));
const AuthConfirmSignUp = lazy(() => import("@pages/auth/ConfirmSignUpPage"));
const AuthSetupMfa = lazy(() => import("@pages/auth/SetupMfaPage"));
const AuthResetPassword = lazy(() => import("@pages/auth/ResetPasswordPage"));

const withSuspense = (element: React.ReactNode) => (
    <Suspense
        fallback={
            <div className="flex items-center justify-center fixed inset-0 z-50 bg-primary-10">
                <Spinner size="lg" variant="primary" />
            </div>
        }
    >
        {element}
    </Suspense>
);

export const routes: RouteObject[] = [
    {
        path: "/login",
        element: (
            <RequireGuest>
                {withSuspense(<AuthLogin />)}
            </RequireGuest>
        ),
    },
    {
        path: "/signup",
        element: (
            <RequireGuest>
                {withSuspense(<AuthSignUp />)}
            </RequireGuest>
        ),
    },
    {
        path: "/forgot-password",
        element: (
            <RequireGuest>
                {withSuspense(<AuthForgotPassword />)}
            </RequireGuest>
        ),
    },
    {
        path: "/authentication-code",
        element: (
            <RequireGuest>
                <RequireCognitoFlow>
                    {withSuspense(<AuthAuthenticationCode />)}
                </RequireCognitoFlow>
            </RequireGuest>
        ),
    },
    {
        path: "/email-verified",
        element: (
            <RequireGuest>
                <RequireCognitoFlow>
                    {withSuspense(<AuthEmailVerified />)}
                </RequireCognitoFlow>
            </RequireGuest>
        ),
    },
    {
        path: "/confirm-signup",
        element: (
            <RequireGuest>
                <RequireCognitoFlow>
                    {withSuspense(<AuthConfirmSignUp />)}
                </RequireCognitoFlow>
            </RequireGuest>
        ),
    },
    {
        path: "/setup-mfa",
        element: (
            <RequireAuth>
                {withSuspense(<AuthSetupMfa />)}
            </RequireAuth>
        ),
    },
    {
        path: "/reset-password",
        element: (
            <RequireGuest>
                {withSuspense(<AuthResetPassword />)}
            </RequireGuest>
        ),
    },
    {
        path: "/",
        element: <RequireAuth>{withSuspense(<Layout />)}</RequireAuth>,
        children: [
            { index: true, element: <Navigate to="/demo-lab" replace /> },
            { path: "demo-lab", element: withSuspense(<DemoLab />) },
            { path: "demo-lab/create", element: withSuspense(<DemoLabCreate />) },
            { path: "demo-lab/edit/:projectId", element: withSuspense(<DemoLabCreate />) },
            { path: "settings", element: withSuspense(<Settings />) },
            { path: "read-me", element: withSuspense(<ReadMe />) },
            {
                path: "portal-lab",
                element: (
                    <RequirePortalLabAccess>
                        {withSuspense(<PortalLabLayout />)}
                    </RequirePortalLabAccess>
                ),
                children: [
                    { index: true, element: withSuspense(<PortalLabList />) },
                    { path: "generate", element: withSuspense(<PortalLabHome />) },
                    { path: "create", element: withSuspense(<PortalLabDefinePortal />) },
                    { path: "edit/:processingId", element: withSuspense(<PortalLabDefinePortal />) },
                ],
            },
            // { path: "mockup-lab", element: withSuspense(<MockupLab />) },
            // { path: "mockup-lab/generate_new", element: withSuspense(<MockupLabCreate />) },
            // { path: "mockup-lab/edit/:projectId", element: withSuspense(<MockupLabCreate />) },
            { path: "integration", element: withSuspense(<Integration />) },
            { path: "integration/create_new_connection", element: withSuspense(<CreateConnection />) },
            {
                path: "integration/create_new_connection/config",
                element: withSuspense(<CreateConnection />),
            },
            { path: "integration/edit-connection/:connectionId", element: withSuspense(<EditConnection />) },
            { path: "integration/create_new_stack", element: withSuspense(<CreateStack />) },
            { path: "integration/edit-stack/:stackId", element: withSuspense(<EditStack />) },
            {
                path: "playground",
                element: (
                    <RequirePlaygroundAccess>
                        {withSuspense(<Playground />)}
                    </RequirePlaygroundAccess>
                ),
            },
        ],
    },
    // {
    //     path: "mockup-lab/view/:templateId",
    //     element: <RequireAuth>{withSuspense(<MockupLabView />)}</RequireAuth>,
    // },
    {
        path: "*",
        element: <PageNotFound />,
    },
];